import { useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { useDisclosure } from '@chakra-ui/react'

import { DateValueI } from '@/components/ui'
import { useGuardToursQuery } from '@/graphql/generated/hooks'
import { getCreatedAtGteTimestamp } from '@/redux/incidents/utils'
import { selectMe } from '@/redux/me/meSlice'
import {
  selectGuardToursFacilityIdsFilter,
  selectGuardToursGuardIdsFilter,
  selectGuardToursStatusFilter,
  selectGuardToursSubmittedAtRangeFilter,
  selectSideNavOpen,
} from '@/redux/ui/uiSlice'
import { FilterOptionI, isCustomDateRange } from '@/utils/filterDrawers'

import { getOrderBy } from '../../guard-tours-table/utils/utils'

interface UseGuardToursArgsI {
  pageIndex: number
  pageSize: number
  sortColumn: string | null
  sortOrder: 'asc' | 'desc' | null
}

export const useGuardTours = ({
  pageIndex,
  pageSize,
  sortColumn,
  sortOrder,
}: UseGuardToursArgsI) => {
  const me = useSelector(selectMe)
  const dispatch = useDispatch()

  const isSideNavOpen = useSelector(selectSideNavOpen)

  const [nameFilter, setNameFilter] = useState(null)
  const [debouncedNameFilter] = useDebounce(nameFilter, 1000)
  const isGuardToursV2 = useFeatureFlag(FeatureFlag.guardToursV2)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const facilityIdsFilterValue = useSelector(selectGuardToursFacilityIdsFilter)
  const guardIdsFilterValue = useSelector(selectGuardToursGuardIdsFilter)
  const submittedAtFilterValue = useSelector(
    selectGuardToursSubmittedAtRangeFilter
  )
  const statusFilterValue = useSelector(selectGuardToursStatusFilter)

  const submittedAtOption = submittedAtFilterValue as FilterOptionI<DateValueI>

  const submittedAtFilter = useMemo(() => {
    if (isCustomDateRange(submittedAtOption)) {
      return {
        submittedAtGte: submittedAtOption?.value?.gte,
        submittedAtLte: submittedAtOption?.value?.lte,
      }
    }

    if (submittedAtFilterValue?.value) {
      return {
        submittedAtGte: getCreatedAtGteTimestamp(
          Date.now(),
          submittedAtFilterValue.value as number
        ),
      }
    }

    return {}
  }, [
    submittedAtFilterValue?.value,
    submittedAtOption?.value?.gte,
    submittedAtOption?.value?.lte,
  ])

  const filters = {
    facilityIdIn: facilityIdsFilterValue?.length
      ? facilityIdsFilterValue?.map((f) => f.value)
      : undefined,
    guardIdIn: guardIdsFilterValue?.length
      ? guardIdsFilterValue?.map((g) => g.value)
      : undefined,

    statusIn: statusFilterValue?.length
      ? statusFilterValue.map((s) => s.value)
      : undefined,

    tourPlanNameLike: debouncedNameFilter,
  }

  const {
    data,
    loading: isGuardToursLoading,
    refetch,
  } = useGuardToursQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    variables: {
      filter: JSON.parse(
        JSON.stringify({
          ...filters,
          ...submittedAtFilter,
        })
      ),
      pageIndex,
      pageSize,
      orderBy: getOrderBy(sortColumn, sortOrder),
    },
  })

  const toursCount = data?.guardTours.totalCount
  const toursList = data?.guardTours.edges ?? []

  return {
    me,
    dispatch,
    isSideNavOpen,
    isGuardToursV2,
    isGuardTourModalOpen: isOpen,
    onGuardTourModalOpen: onOpen,
    onGuardTourModalClose: onClose,
    data,
    isGuardToursLoading,
    refetch,
    nameFilter,
    setNameFilter,
    toursCount,
    toursList,
    filters,
  }
}
